/**
 * The EAN line item property. Every add to cart sends the barcode of the variant being added as
 * the visible property "EAN", so it lands on the order, the packing slip, the confirmation and in
 * Sendcloud. A blank barcode (the gift wrap product, say) sends nothing.
 */

export const EAN_PROPERTY_NAME = 'EAN';
export const EAN_FIELD = `properties[${EAN_PROPERTY_NAME}]`;

/**
 * @param {unknown} barcode
 * @returns {string} The trimmed barcode, or '' when there is none.
 */
export const cleanEan = (barcode) => (barcode == null ? '' : String(barcode).trim());

/**
 * Sets or removes properties[EAN] on a FormData, so a blank barcode never leaves a stale or empty
 * property behind. Other properties are never touched.
 *
 * @param {FormData} formData
 * @param {unknown} barcode
 */
export const setEanOnFormData = (formData, barcode) => {
  const ean = cleanEan(barcode);
  if (ean) formData.set(EAN_FIELD, ean);
  else formData.delete(EAN_FIELD);
};

/**
 * Adds the EAN to a properties object for a JSON items[] payload, keeping every other property.
 *
 * @param {Record<string, string> | undefined} properties
 * @param {unknown} barcode
 * @returns {Record<string, string>}
 */
export const withEan = (properties, barcode) => {
  const ean = cleanEan(barcode);
  return ean ? { ...properties, [EAN_PROPERTY_NAME]: ean } : { ...properties };
};

/**
 * Reads the barcode (data-ean) of the variant a control stands for: a button or radio carries it
 * itself, a select carries it on its selected option.
 *
 * @param {{ dataset?: Record<string, string | undefined>, selectedOptions?: ArrayLike<{ dataset?: Record<string, string | undefined> }> } | null | undefined} control
 * @returns {string}
 */
export const eanOfControl = (control) => {
  if (!control) return '';
  const own = control.dataset?.ean;
  if (own != null) return cleanEan(own);
  return cleanEan(control.selectedOptions?.[0]?.dataset?.ean);
};

/**
 * Points a hidden properties[EAN] input at the barcode of a variant control. A blank barcode
 * disables the input, so no EAN property is submitted at all.
 *
 * @param {HTMLInputElement | null | undefined} eanInput
 * @param {Parameters<typeof eanOfControl>[0]} control
 */
export const syncEanInput = (eanInput, control) => {
  if (!eanInput) return;
  const ean = eanOfControl(control);
  eanInput.value = ean;
  eanInput.disabled = ean === '';
};

/**
 * Snapshots a variant id together with the EAN of the control it came from, so both are read at
 * the same moment and stay paired even if the control changes afterwards.
 *
 * @param {string | undefined} variantId
 * @param {Parameters<typeof eanOfControl>[0]} control
 * @returns {{ variantId: string | undefined, ean: string }}
 */
export const captureExtra = (variantId, control) => ({ variantId, ean: eanOfControl(control) });
